/**
 * The BCP-47 locale the browser should format dates, times and numbers with.
 *
 * Most formatting happens in module-level helpers that cannot reach the
 * LanguageContext hook, so — like `runtimeTranslation` — the provider pushes
 * the current language here and the helpers read it back.
 *
 * Only the client sets this. On the server the module keeps its default so
 * one request can never observe another request's language, and because the
 * provider seeds it during the first client render, values formatted after
 * mount already use the right locale.
 */

/** Languages we pin to a region, so the date order and clock match what the UI used before. */
const REGIONAL_TAGS: Record<string, string> = {
  tr: 'tr-TR',
  en: 'en-US',
};

export const DEFAULT_LOCALE_TAG = 'tr-TR';

let runtimeLocale = DEFAULT_LOCALE_TAG;

export function setRuntimeLocale(lang: string | null | undefined): void {
  const code = (lang ?? '').trim().toLowerCase();
  if (!code) {
    runtimeLocale = DEFAULT_LOCALE_TAG;
    return;
  }
  const primary = code.split('-')[0];
  // Anything else is handed to Intl as-is: a bare subtag like `de` or `ar` is
  // a valid locale, so a language the operator adds later formats correctly
  // without needing an entry above.
  runtimeLocale = REGIONAL_TAGS[primary]
    ?? (/^[a-z]{2,3}(-[a-z0-9]{2,8})?$/.test(code) ? code : DEFAULT_LOCALE_TAG);
}

/** Current locale tag, safe to pass straight to `toLocale*` and `Intl.*`. */
export function localeTag(): string {
  return runtimeLocale;
}
